import { type FC, useEffect, useState } from 'react'
import type { NotificationMode } from '@opallabs/concepts'
import { SimpleButton } from '@opallabs/lapidary'
import { NotificationModeMenu } from './NotificationModeMenu'
import * as styles from './styles'

type NotificationFooterProps = Readonly<{
  /** True while the stored selection is not yet resolved to picker options. */
  isNotifyDisabled?: boolean
  mode: NotificationMode
  onModeChange: (mode: NotificationMode) => void
  onNotify: () => Promise<void>
  /** A user group counts as one. */
  pendingCount: number
  /** The users and user groups selected in the picker right now. */
  selectedIds: ReadonlyArray<string>
}>

/**
 * The notification controls inside an open People Picker. Presentational: the
 * surface owns the requests, since Plans writes to Loupe and Boards to the monolith.
 */
export const NotificationFooter: FC<NotificationFooterProps> = ({
  isNotifyDisabled = false,
  mode,
  onModeChange,
  onNotify,
  pendingCount,
  selectedIds,
}) => {
  const hasPending = mode === 'manual' && pendingCount > 0
  // "Notified" confirms a send made while the menu is open: a Notify click in
  // manual mode, or adding someone in automatic mode. Closing the menu clears
  // it, and so does adding a pending person in manual mode.
  const [isNotified, setIsNotified] = useState(false)
  const [isSending, setIsSending] = useState(false)
  // The footer mounts when the menu opens, so this is the opening selection.
  const [initialIds] = useState(() => new Set(selectedIds))
  const isAutoNotified =
    mode === 'automatic' && selectedIds.some((id) => !initialIds.has(id))

  useEffect(() => {
    if (pendingCount > 0) setIsNotified(false)
  }, [pendingCount])

  return (
    <div css={styles.notificationFooter}>
      <NotificationModeMenu mode={mode} onModeChange={onModeChange} />

      {hasPending && (
        <div css={styles.sendState}>
          <span css={styles.pendingCount}>{`${pendingCount} pending`}</span>
          <SimpleButton
            disabled={isNotifyDisabled || isSending}
            size="small"
            onClick={() => {
              setIsSending(true)
              setIsNotified(true)
              onNotify().finally(() => setIsSending(false))
            }}
            variant="tertiary"
          >
            Notify
          </SimpleButton>
        </div>
      )}
      {(isAutoNotified || (isNotified && !hasPending)) && (
        <span css={styles.notified}>Notified</span>
      )}
    </div>
  )
}
NotificationFooter.displayName = 'Satellite: NotificationFooter ← CustomFields'

/** The pending count on a closed People Picker. */
export const PendingBadge: FC<{ count: number }> = ({ count }) => (
  <span css={styles.pendingBadge}>{count}</span>
)
PendingBadge.displayName = 'Satellite: PendingBadge ← CustomFields'
